/**
 * OrgSwitcher
 *
 * Sidebar-mounted picker for users who are members of more than one
 * organization (ADR 0016, Phase B Stufe 1).
 *
 * Phase-1 contract:
 *   - Reads memberships from `org_memberships` (ADR 0016).
 *   - Single-membership case: renders nothing — the existing org-name
 *     in the sidebar header is enough.
 *   - Multi-membership case: dropdown with all orgs the user belongs
 *     to. Picking a different one updates `profiles.organization_id`
 *     and refreshes the page so React-Query cache + RLS-derived
 *     `get_my_org_id()` re-resolve cleanly.
 *
 * Phase-2 will replace the profile-update with a JWT custom claim
 * + cache-only invalidate (no full reload). The component contract
 * stays the same, only the `applySwitch` implementation changes.
 *
 * Demo mode: hidden — the demo org is fixed and switching would
 * make no sense.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { orgMembershipsRepo, profilesRepo } from '@/data';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function OrgSwitcher({ className }: { className?: string }) {
  const { profile } = useAuth();
  const { isDemo } = useDemo();
  const qc = useQueryClient();

  const membershipsQuery = useQuery({
    queryKey: ['org-memberships', profile?.user_id],
    enabled: !isDemo && Boolean(profile?.user_id),
    queryFn: () => orgMembershipsRepo.listMyMemberships(),
    // Memberships rarely change — refetch on focus is overkill.
    staleTime: 5 * 60 * 1000,
  });

  const switchMutation = useMutation({
    mutationFn: async (newOrgId: string) => {
      if (!profile?.user_id) throw new Error('No active session');
      await profilesRepo.updateProfile(profile.user_id, { organization_id: newOrgId });
    },
    onSuccess: () => {
      // Clearing the cache + reloading is the surgical way to make
      // sure no stale row from the previous org sneaks into the new
      // session. Phase 2 will replace this with a JWT-claim swap +
      // cache invalidation only.
      qc.clear();
      toast.success('Org gewechselt — lade neu …');
      setTimeout(() => window.location.reload(), 200);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Wechsel fehlgeschlagen';
      toast.error(msg);
    },
  });

  if (isDemo) return null;
  const memberships = membershipsQuery.data ?? [];

  // Phase 1: only render when the user actually has more than one
  // membership. Single-org users see nothing — sidebar org name is
  // already in the header.
  if (memberships.length <= 1) return null;

  const activeOrgId = profile?.organization_id ?? null;
  const activeMembership =
    memberships.find((m) => m.organization_id === activeOrgId) ?? memberships[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`group inline-flex w-full items-center gap-2 rounded-lg border border-border/40 bg-card hover:bg-secondary/40 px-2.5 py-1.5 text-left text-xs transition-colors ${
            className ?? ''
          }`}
          aria-label="Organisation wechseln"
        >
          <Building2 className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="flex-1 min-w-0 truncate font-medium">
            {activeMembership.organization.name}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Meine Organisationen
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {memberships.map((m) => {
          const active = m.organization_id === activeOrgId;
          return (
            <DropdownMenuItem
              key={m.id}
              onSelect={() => {
                if (active || switchMutation.isPending) return;
                switchMutation.mutate(m.organization_id);
              }}
              disabled={switchMutation.isPending}
              className="flex items-start gap-2"
            >
              <div className="w-4 mt-0.5 shrink-0">
                {active ? (
                  <Check className="w-4 h-4 text-primary" />
                ) : switchMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.organization.name}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  Rolle: {m.role}
                  {m.organization.setting_type
                    ? ` · ${m.organization.setting_type === 'klinik' ? 'Klinik' : 'Praxis'}`
                    : ''}
                  {m.is_default ? ' · Standard' : ''}
                </p>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
